import { Injectable } from '@angular/core';
import { getHttpStatusMessage } from '@r1-compass-library/constants/http-status.constant';
import { ApiError, ApiErrorResponse, ErrorSnackBarData, ErrorSnackBarOptions } from '@r1-compass-library/models';
import { DataMartError, DataMartErrorResponse } from '@r1-compass-library/models/data-mart/data-mart-error.model';
import { RequiredFields } from '@r1-compass-library/utilities';
import { Observable, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ErrorHandlingService {
    readonly genericMessage: string = 'Something went wrong.';

    readonly genericErrorData: ApiErrorResponse = { message: this.genericMessage, status: 500 };
    readonly genericDataMartErrorResponse: DataMartErrorResponse = {
        errorMessage: this.genericMessage,
        statusCode: 500,
        statusText: 'Internal Server Error',
    };

    public handleApiError(error: any): Observable<never> {
        const errorData: ApiErrorResponse = error
            ? error.error
                ? error.error
                : { message: error.message, status: error.status, error: error.statusText }
            : this.genericErrorData;

        return throwError(() => new ApiError(errorData));
    }

    public displayApiError(config: { error: ApiError; type: 'snackbar' | 'embed' }): ErrorSnackBarData {
        const errorData = this.createErrorData({
            statusCode: config.error.response.status,
            statusText: config.error.response.error ?? '',
            messages: [config.error.response.message],
            showDismissButton: true,
        });
        if (config.type === 'snackbar') {
            return errorData;
        } else {
            return { ...errorData, showDismissButton: false };
        }
    }

    public handleDataMartApiError(error: any): Observable<never> {
        return throwError(() => new DataMartError(this.buildDataMartErrorResponse(error)));
    }

    public displayDataMartApiError(config: { error: DataMartError; type: 'snackbar' | 'embed' }): ErrorSnackBarData {
        const errorData = this.createErrorData({
            statusCode: config.error.response.statusCode,
            statusText: getHttpStatusMessage(config.error.response.statusCode), // Sometimes the datamart API returns the wrong status text (ex: code 400 with "OK" text), so we generate it ourselves
            messages: ['Error Type: ' + (config.error.response.errorType ?? this.genericMessage)],
            expandMessages: [config.error.response.errorMessage, config.error.response.stackTrace ?? ''],
            showDismissButton: true,
            showExpandButton: true,
        });
        if (config.type === 'snackbar') {
            return errorData;
        } else {
            return { ...errorData, showDismissButton: false };
        }
    }

    public createErrorData(
        options: ErrorSnackBarOptions
    ): RequiredFields<ErrorSnackBarData, 'statusCode' | 'statusText' | 'messages'> {
        const statusCode: number = options.statusCode ?? 500;
        const statusText: string = options.statusText ?? '';
        const messages: string[] = options.messages ? [...options.messages] : [];
        const expandMessages: string[] = options.expandMessages ? [...options.expandMessages] : [];
        const showDismissButton = !!options.showDismissButton;
        const showExpandButton = !!options.showExpandButton;

        return {
            statusCode: statusCode,
            statusText: statusText,
            messages: messages,
            expandMessages: expandMessages,
            showDismissButton: showDismissButton,
            showExpandButton: showExpandButton,
        };
    }

    private buildDataMartErrorResponse(error: any): DataMartErrorResponse {
        if (error) {
            if (error.error) {
                return { ...error.error, statusCode: error.status, statusText: error.statusText };
            } else {
                return { errorMessage: error.message, statusCode: error.status, statusText: error.statusText };
            }
        } else {
            return this.genericDataMartErrorResponse;
        }
    }
}
